import { useEffect, useRef, useState } from "react";
import MapView from "@arcgis/core/views/MapView";
import Map from "@arcgis/core/Map";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import VectorTileLayer from "@arcgis/core/layers/VectorTileLayer";
import Sketch from "@arcgis/core/widgets/Sketch";
import Zoom from "@arcgis/core/widgets/Zoom";
import ArcgisConfig from "@arcgis/core/config";
import AppSettings from "../../../config";

interface MapInitOptions {
  apiKey?: string;
  center: [number, number];
  zoom?: number;
  basemap?: string;
  additionalVectorLayers?: string[];
  sketchOptions?: Record<string, any>;
  constraints?: __esri.View2DConstraintsProperties;
  navigation?: __esri.NavigationProperties;
}

export function useMapInitialization({
  apiKey = AppSettings.ARCGIS_API_KEY,
  center,
  zoom = 12,
  basemap = "streets-navigation-vector",
  additionalVectorLayers = [],
  sketchOptions = {},
  constraints,
  navigation,
}: MapInitOptions) {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<MapView | null>(null);
  const sketchRef = useRef<Sketch | null>(null);
  const readonlySketchRef = useRef<Sketch | null>(null); // Readonly layers still need sketch so we can detect clicks without hit testing
  const [sketchInitialized, setSketchInitialized] = useState(false);
  const [readonlySketchInitialized, setReadonlySketchInitialized] = useState(false);
  const editLayerRef = useRef<GraphicsLayer | null>(null);
  const readonlyLayerRef = useRef<GraphicsLayer | null>(null);
  const zoomRef = useRef<Zoom | null>(null);

  useEffect(() => {
    if (!mapRef.current) return;
    ArcgisConfig.apiKey = apiKey;

    const editLayer = new GraphicsLayer({
      id: "editLayer",
    });
    editLayerRef.current = editLayer;

    const readonlyLayer = new GraphicsLayer({
      id: "readonlyLayer",
    });
    readonlyLayerRef.current = readonlyLayer;

    // Create vector tile layers (if provided in options)
    const additionalLayers = [];
    for (const vtl of additionalVectorLayers) {
      const vtlLayer = new VectorTileLayer({
        url: vtl,
      });
      vtlLayer.opacity = 0.5;
      additionalLayers.push(vtlLayer);
    }

    const map = new Map({
      basemap,
      layers: [...additionalLayers, readonlyLayer, editLayer],
    });
    const view = new MapView({
      container: mapRef.current,
      center,
      zoom,
      ...(constraints ? { constraints } : {}),
      ...(navigation ? { navigation } : {}),
      map,
    });
    viewRef.current = view;

    view.when(() => {
      const sketch = new Sketch({
        layer: editLayer,
        view: view,
        creationMode: "update",
        layout: "horizontal",
        toolbarKind: "floating",
        visibleElements: {
          createTools: {
            point: false,
            polyline: false,
            polygon: true,
            rectangle: true,
            circle: true,
          },
          selectionTools: {
            "lasso-selection": true,
            "rectangle-selection": true,
          },
          settingsMenu: false,
          undoRedoMenu: true,
        },
        visible: false,
        ...sketchOptions,
      });
      sketch.when(() => {
        setSketchInitialized(true);
      });
      sketchRef.current = sketch;
      view.ui.add(sketch, {
        position: "top-left",
        index: 0,
      });

      const readonlySketch = new Sketch({
        layer: readonlyLayer,
        view: view,
        creationMode: "update",
        visible: false,
      });
      readonlySketch.when(() => {
        setReadonlySketchInitialized(true);
      });
      readonlySketchRef.current = readonlySketch;
      view.ui.add(readonlySketch, {
        position: "top-left",
        index: 0,
      });

      view.ui.move("zoom", "bottom-right");
      zoomRef.current = view.ui.getComponents("bottom-right")[0] as Zoom;
      zoomRef.current.visible = false;
    });

    return () => {
      if (viewRef.current) {
        viewRef.current.destroy();
      }
    };
  }, [!!mapRef.current]);

  useEffect(() => {
    if (viewRef.current) viewRef.current.center = center;
  }, [center]);

  return {
    mapRef,
    viewRef,
    sketchRef,
    sketchInitialized,
    readonlySketchRef,
    readonlySketchInitialized,
    editLayerRef,
    readonlyLayerRef,
    zoomRef,
  };
}
